import _ from "lodash";

import {
  GET_SCHEDULES_SUCCESS,
  ADD_SCHEDULE_RULE,
  CHANGE_CATEGORIES,
  SchedulesState,
  SAVE_SCHEDULES_SUCCESS,
  EDIT_SCHEDULE_RULE,
  DELETE_SCHEDULE_RULE,
  DISCARD_SCHEDULE_CHANGES,
  PAUSE_INTERNET_SUCCESS,
  RESUME_INTERNET_SUCCESS,
  TOGGLE_SCHEDULE_ENABLED,
} from "./schedules.types";

import {
  addItemsToList,
  changeItemInList,
  removeItemsFromList,
} from "./schedules.helper";
import { SCHEDULE_TYPES } from "../../pages/profile/scheduler/scheduler.consts";
import { FINISH_WIZARD } from "../../pages/wizard/store/wizard.actions";
import { ADD_PROFILE_SUCCESS } from "../profiles";
import { ScheduleStore } from "@sportal/api";

const initialState: SchedulesState = {
  saved: {
    list: {},
    keys: [],
    blockedCategories: {},
  },
  changed: {
    list: {},
    keys: [],
    blockedCategories: {},
  },
};

export const schedulesReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case FINISH_WIZARD: {
      const { keys } = payload.profiles;

      const blockedCategories = keys.reduce(
        (acc: Record<string, string[]>, profileId: string) => {
          acc[profileId] = [];
          return acc;
        },
        {} as Record<string, string[]>,
      );

      return {
        ...state,
        saved: {
          ...state.saved,
          blockedCategories,
        },
        changed: {
          ...state.changed,
          blockedCategories,
        },
      };
    }
    case ADD_PROFILE_SUCCESS: {
      const { id: profileId } = payload;

      return {
        ...state,
        saved: {
          ...state.saved,
          blockedCategories: {
            ...state.saved.blockedCategories,
            [profileId]: [],
          },
        },
        changed: {
          ...state.changed,
          blockedCategories: {
            ...state.changed.blockedCategories,
            [profileId]: [],
          },
        },
      };
    }
    case GET_SCHEDULES_SUCCESS: {
      const scheduleRules = [];
      const blockedCategories = {};

      payload.forEach(({ id, schedules }) => {
        blockedCategories[id] =
          _.find(schedules, { type: SCHEDULE_TYPES.homework })?.categories ||
          [];
        scheduleRules.push(
          ..._.map(schedules, (schedule) => ({
            ..._.omit(schedule, "categories"),
            profile: id,
          })),
        );
      });

      const normalizedSchedules = addItemsToList(scheduleRules, {
        list: {},
        keys: [],
      });

      return {
        ...state,
        saved: {
          ...normalizedSchedules,
          blockedCategories,
        },
        changed: {
          ...normalizedSchedules,
          blockedCategories,
        },
      };
    }
    case ADD_SCHEDULE_RULE:
      return {
        ...state,
        changed: {
          ...state.changed,
          ...addItemsToList([payload], state.changed),
        },
      };
    case EDIT_SCHEDULE_RULE:
      return {
        ...state,
        changed: {
          ...state.changed,
          ...changeItemInList(payload, state.changed),
        },
      };
    case TOGGLE_SCHEDULE_ENABLED: {
      const { profileId, type, enabled } = payload;

      const updatedChanged = _(state.changed.list)
        .pickBy(
          (schedule) =>
            schedule.profile === profileId && schedule.type === type,
        )
        .mapValues((schedule) => ({ ...schedule, enabled }))
        .value();

      return {
        ...state,
        changed: {
          ...state.changed,
          list: {
            ...state.changed.list,
            ...updatedChanged,
          },
        },
      };
    }

    case DELETE_SCHEDULE_RULE:
      return {
        ...state,
        changed: {
          ...state.changed,
          ...removeItemsFromList([payload.id], state.changed),
        },
      };
    case CHANGE_CATEGORIES: {
      const { categories, profileId } = payload;

      return {
        ...state,
        changed: {
          ...state.changed,
          blockedCategories: {
            ...state.changed.blockedCategories,
            [profileId]: categories,
          },
        },
      };
    }
    case SAVE_SCHEDULES_SUCCESS: {
      const { type, profileId, removed } = payload;
      const toRemove = _.map(removed, "id");

      const schedulesFromChangedToSave = _(state.changed.list)
        .filter(
          (schedule) =>
            schedule.profile === profileId && schedule.type === type,
        )
        .value();

      const withRemovedSchedules = removeItemsFromList(toRemove, state.saved);
      const withChangedSchedules = addItemsToList(
        schedulesFromChangedToSave,
        withRemovedSchedules,
      );

      const hasRemainingHomeworkSchedulesForProfile = Object.values(
        withChangedSchedules.list,
      ).some(
        (s: ScheduleStore) =>
          s.profile === profileId && s.type === SCHEDULE_TYPES.homework,
      );

      const savedCategories =
        type === SCHEDULE_TYPES.homework
          ? {
              ...state.changed.blockedCategories,
              ...(!hasRemainingHomeworkSchedulesForProfile && {
                [profileId]: [],
              }),
            }
          : {
              ...state.saved.blockedCategories,
            };
      const changedCategories =
        type === SCHEDULE_TYPES.homework
          ? {
              ...state.changed.blockedCategories,
              ...(!hasRemainingHomeworkSchedulesForProfile && {
                [profileId]: [],
              }),
            }
          : {
              ...state.changed.blockedCategories,
            };

      return {
        ...state,
        saved: {
          ...withChangedSchedules,
          blockedCategories: { ...savedCategories },
        },
        changed: {
          ...state.changed,
          blockedCategories: { ...changedCategories },
        },
      };
    }
    case DISCARD_SCHEDULE_CHANGES: {
      const { profileId, type } = payload;
      const schedulesToRestore = _(state.saved.list)
        .filter(
          (schedule) =>
            schedule.profile === profileId && schedule.type === type,
        )
        .value();

      const otherChangedSchedules = _(state.changed.list)
        .filter(
          (schedule) =>
            !(schedule.profile === profileId && schedule.type === type),
        )
        .value();

      const blockedCategories =
        type === SCHEDULE_TYPES.homework
          ? state.saved.blockedCategories
          : state.changed.blockedCategories;

      const keysToKeep = _.union(
        _.map(schedulesToRestore, "id"),
        _.map(otherChangedSchedules, "id"),
      );
      const scheduleListToKeep = {
        ..._.keyBy(schedulesToRestore, "id"),
        ..._.keyBy(otherChangedSchedules, "id"),
      };

      return {
        ...state,
        changed: {
          keys: keysToKeep,
          list: scheduleListToKeep,
          blockedCategories: {
            ...state.changed.blockedCategories,
            [profileId]: blockedCategories[profileId] || [],
          },
        },
      };
    }

    case PAUSE_INTERNET_SUCCESS: {
      const { profileId, schedule } = payload;
      const updatedSchedule = { ...schedule, profile: profileId };

      return {
        ...state,
        changed: {
          ...state.changed,
          ...addItemsToList([updatedSchedule], state.changed),
        },
      };
    }
    case RESUME_INTERNET_SUCCESS: {
      const { scheduleId } = payload;
      const changed = removeItemsFromList([scheduleId], state.changed);

      return {
        ...state,
        changed: {
          ...state.changed,
          ...changed,
        },
      };
    }
    default:
      return state;
  }
};
